import { useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  ServerError,
} from "@apollo/client";
import { LocalState } from "@apollo/client/local-state";

import { ErrorLink } from "@apollo/client/link/error";
import { SetContextLink } from "@apollo/client/link/context";
import { cache } from "../cache";
import UploadLink from "apollo-upload-client/UploadHttpLink.mjs";
import { GRAPHQL_URL, DEBUG, VERSION } from "../settings";
import { APOLLO_ERR_CODE } from "../utils/constants";
import { errorLogger } from "../utils/logger";
import type { GraphQLFormattedError } from "graphql";

export const useApolloClient = () => {
  type ErrorLinkStatus = {
    sessionExpired: boolean;
    notFound: boolean;
    forbidden: boolean;
    serverError: boolean;
    notSameSubDomain: boolean;
  };
  const [errorLinkStatus, setErrorLinkStatus] = useState<ErrorLinkStatus>({
    sessionExpired: false,
    notFound: false,
    forbidden: false,
    serverError: false,
    notSameSubDomain: false,
  });

  const authLink = new SetContextLink(({ headers }) => {
    // Return the headers to the context so httpLink can read them
    return new Promise(async (resolve, reject) => {
      try {
        const { tokens } = await fetchAuthSession();
        resolve({
          headers: {
            ...headers,
            authorization: tokens?.idToken
              ? `Bearer ${tokens.idToken.toString()}`
              : "",
            "x-dtf-client-name": "cs-system-client",
            "x-dtf-client-version": VERSION ?? "",
          },
        });
      } catch (err) {
        reject(err);
      }
    });
  });

  const linkHandleErrorByErrorCode = (
    errorCode: keyof typeof APOLLO_ERR_CODE,
  ) => {
    switch (errorCode) {
      case APOLLO_ERR_CODE.UNAUTHENTICATED:
        setErrorLinkStatus({ ...errorLinkStatus, sessionExpired: true });
        return;
      case APOLLO_ERR_CODE.NOT_FOUND:
        setErrorLinkStatus({ ...errorLinkStatus, notFound: true });
        return;
      case APOLLO_ERR_CODE.FORBIDDEN:
        setErrorLinkStatus({ ...errorLinkStatus, forbidden: true });
        return;
      case APOLLO_ERR_CODE.PDT_ERROR:
      case APOLLO_ERR_CODE.INVALID_ARGS:
        setErrorLinkStatus({ ...errorLinkStatus, serverError: true });
        return;
      case APOLLO_ERR_CODE.NOT_SAME_SUBDOMAIN:
        setErrorLinkStatus({ ...errorLinkStatus, notSameSubDomain: true });
        return;
    }
  };

  /**
   * 不必要なerrorまでsentryに流したり、コンソールに表示することを避けるためのメソッドです
   * @param error {GraphQLError}
   */
  const shouldShowError = (error: GraphQLFormattedError) => {
    const existExtensions = !!error.extensions && !!error.extensions.code;
    // 以下のエラー群はsentryに流す必要がないため、エラーを投げないようにしています。
    return (
      existExtensions &&
      error.extensions?.code !== APOLLO_ERR_CODE.UNAUTHENTICATED &&
      error.extensions?.code !== APOLLO_ERR_CODE.NOT_AUTHORIZED &&
      error.extensions?.code !== APOLLO_ERR_CODE.NOT_AUTHENTICATED
    );
  };

  const errorLink = new ErrorLink(({ error }) => {
    if (DEBUG !== "*") {
      // TODO: 以下の仕様を使って、Production時に"ApolloError"のエラーがDeveloper Tools上に出ないようにどのエラーでもignoreする
      // https://www.apollographql.com/docs/react/data/error-handling/#ignoring-errors
      // @ts-ignore
      // response.errors = null;
      return;
    }
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.map(({ extensions }) => {
        extensions &&
          extensions.code &&
          linkHandleErrorByErrorCode(
            extensions.code as keyof typeof APOLLO_ERR_CODE,
          );
      });
      error.errors.map((formattedError) => {
        if (!shouldShowError(formattedError)) {
          return;
        }
        errorLogger(
          `[GraphQL Error] ${formattedError.extensions?.code}: ${formattedError.message}`,
        );
      });
    }

    if (ServerError.is(error)) {
      errorLogger(error);
    }
  });

  const link = ApolloLink.from([
    authLink,
    errorLink,
    new UploadLink({
      uri: GRAPHQL_URL,
    }),
  ]);

  return {
    apolloClient: new ApolloClient({
      cache,
      link,
      localState: new LocalState(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
          errorPolicy: "all",
        },
      },
    }),
    errorLinkStatus,
  };
};
